import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Wallet, 
  PieChart, 
  Users, 
  FileText, 
  FileCheck,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart as RechartsPie, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend,
  ComposedChart,
  Area
} from "recharts";
import i18n from "@/i18n";

const formatCurrency = (value) => {
  const locale = i18n.language === 'en' ? 'en-US' : 'es-CO';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currencyDisplay: 'narrowSymbol'
  }).format(value);
};

const formatCurrencyShort = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return formatCurrency(value);
};

const COLORS = ['#0f2a4a', '#3b82f6', '#64748b', '#f59e0b', '#16a34a', '#8b5cf6'];

export default function Metricas() {
  const { t } = useTranslation();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await axios.get(`${API}/stats/business-metrics`);
        setMetrics(response.data);
      } catch (error) {
        toast.error(t('metrics.loadError'));
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">{t('metrics.noMetrics')}</p>
      </div>
    );
  }

  const { resumen, tendencia_mensual, gastos_por_proveedor, gastos_por_tipo_servicio, top_clientes } = metrics;

  return (
    <div data-testid="metricas-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-['Barlow']">{t('metrics.title')}</h1>
        <p className="text-slate-500 mt-1">{t('metrics.subtitle')}</p>
      </div>

      {/* Main KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-slate-200 overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">{t('metrics.totalRevenue')}</p>
                <p className="text-2xl font-bold text-slate-900 mt-2 font-['Barlow']">{formatCurrency(resumen.total_ingresos)}</p>
                <p className="text-xs text-slate-500 mt-1">{resumen.total_facturas} {t('metrics.invoices')}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-100 text-green-600"><TrendingUp className="w-5 h-5" /></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">{t('metrics.totalExpenses')}</p>
                <p className="text-2xl font-bold text-slate-900 mt-2 font-['Barlow']">{formatCurrency(resumen.total_gastos)}</p>
                <p className="text-xs text-slate-500 mt-1">{resumen.total_documentos} {t('metrics.documents')}</p>
              </div>
              <div className="p-3 rounded-lg bg-red-100 text-red-600"><TrendingDown className="w-5 h-5" /></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">{t('metrics.netBalance')}</p>
                <p className={`text-2xl font-bold mt-2 font-['Barlow'] ${resumen.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(resumen.balance)}</p>
                <div className="flex items-center gap-1 mt-1">
                  {resumen.balance >= 0 ? <ArrowUpRight className="w-3 h-3 text-green-500" /> : <ArrowDownRight className="w-3 h-3 text-red-500" />}
                  <span className={`text-xs ${resumen.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>{t('metrics.revenueMinusExpenses')}</span>
                </div>
              </div>
              <div className={`p-3 rounded-lg ${resumen.balance >= 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}><Wallet className="w-5 h-5" /></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">{t('metrics.operationalMargin')}</p>
                <p className={`text-2xl font-bold mt-2 font-['Barlow'] ${resumen.margen_porcentaje >= 0 ? 'text-green-600' : 'text-red-600'}`}>{resumen.margen_porcentaje}%</p>
                <p className="text-xs text-slate-500 mt-1">{t('metrics.ofTotalBilled')}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-100 text-blue-600"><PieChart className="w-5 h-5" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Trend Chart */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold text-slate-900 font-['Barlow']">{t('metrics.monthlyTrend')}</CardTitle>
          <p className="text-sm text-slate-500">{t('metrics.last6Months')}</p>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={tendencia_mensual}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="mes" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} tickFormatter={formatCurrencyShort} />
                <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px' }} formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Area type="monotone" dataKey="ingresos" fill="#dcfce7" stroke="#16a34a" strokeWidth={2} name={t('metrics.revenue')} />
                <Bar dataKey="gastos" fill="#f59e0b" radius={[4, 4, 0, 0]} name={t('metrics.expenses')} />
                <Line type="monotone" dataKey="balance" stroke="#0f2a4a" strokeWidth={2} dot={{ fill: '#0f2a4a', strokeWidth: 2 }} name={t('metrics.balance')} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expenses by Provider */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900 font-['Barlow'] flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-600" />{t('metrics.expensesBySupplier')}
            </CardTitle>
            <p className="text-sm text-slate-500">{t('metrics.top10Suppliers')}</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {gastos_por_proveedor.map((proveedor, index) => {
                const percentage = (proveedor.total / resumen.total_gastos * 100).toFixed(1);
                return (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-700 font-medium truncate max-w-[200px]">{proveedor.nombre}</span>
                      <span className="text-slate-900 font-semibold">{formatCurrency(proveedor.total)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${percentage}%` }} />
                      </div>
                      <span className="text-xs text-slate-500 w-12 text-right">{percentage}%</span>
                    </div>
                    <p className="text-xs text-slate-400">{proveedor.cantidad} {t('metrics.documents')}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Expenses by Service Type */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900 font-['Barlow'] flex items-center gap-2">
              <PieChart className="w-5 h-5 text-blue-600" />{t('metrics.expensesByServiceType')}
            </CardTitle>
            <p className="text-sm text-slate-500">{t('metrics.expenseDistribution')}</p>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={gastos_por_tipo_servicio}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="total"
                    nameKey="tipo"
                    label={({ tipo, percent }) => `${tipo} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {gastos_por_tipo_servicio.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {gastos_por_tipo_servicio.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                  <span className="text-xs text-slate-600">{item.tipo}</span>
                  <span className="text-xs text-slate-900 font-medium ml-auto">{formatCurrencyShort(item.total)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Clients */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold text-slate-900 font-['Barlow'] flex items-center gap-2">
            <FileText className="w-5 h-5 text-green-600" />{t('metrics.topClientsByRevenue')}
          </CardTitle>
          <p className="text-sm text-slate-500">{t('metrics.topClientsSubtitle')}</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{t('metrics.client')}</th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{t('metrics.invoicesCount')}</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{t('metrics.totalBilled')}</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{t('metrics.percentOfTotal')}</th>
                </tr>
              </thead>
              <tbody>
                {top_clientes.map((cliente, index) => {
                  const percentage = (cliente.total / resumen.total_ingresos * 100).toFixed(1);
                  return (
                    <tr key={index} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs font-bold">{index + 1}</div>
                          <span className="font-medium text-slate-900">{cliente.nombre}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center text-slate-600">{cliente.cantidad}</td>
                      <td className="py-3 px-4 text-right font-semibold text-slate-900">{formatCurrency(cliente.total)}</td>
                      <td className="py-3 px-4 text-right">
                        <span className="inline-flex items-center px-2 py-1 rounded bg-green-100 text-green-700 text-xs font-medium">{percentage}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Business Summary */}
      <Card className="border-slate-200 border-l-4 border-l-blue-500">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-blue-100 text-blue-600"><DollarSign className="w-6 h-6" /></div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 font-['Barlow'] mb-2">{t('metrics.financialSummary')}</h3>
              <p className="text-slate-700">
                {t('metrics.summaryText', {
                  revenue: formatCurrency(resumen.total_ingresos),
                  invoiceCount: resumen.total_facturas,
                  expenses: formatCurrency(resumen.total_gastos),
                  docCount: resumen.total_documentos,
                  balance: formatCurrency(resumen.balance),
                  margin: resumen.margen_porcentaje
                })}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
